import { Component, OnInit, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { PostService, Post } from '../../services/post';
import { MetaService } from '../../services/meta.service';

@Component({
	selector: 'app-posts',
	imports: [CommonModule, RouterLink],
	templateUrl: './posts.html',
	styleUrl: './posts.css',
	standalone: true,
})
export class Posts implements OnInit {
	private blogService = inject(PostService);
	private metaService = inject(MetaService);

	blogs = signal<Post[]>([]);
	featuredBlogs = signal<Post[]>([]);
	searchQuery = signal<string>('');
	isLoading = signal<boolean>(true);
	isLoadingMore = signal<boolean>(false);
	errorMessage = signal<string>('');
	
	// Pagination
	currentPage = signal<number>(1);
	hasMore = signal<boolean>(false);
	totalPosts = signal<number>(0);

	tagOptions = ['Recent', 'Men', 'Women', 'Featured'];
	selectedTag = signal<string>('Recent');

	// Debounce timer for search
	private searchDebounceTimer: any = null;

	ngOnInit() {
		this.metaService.setPageMeta('blog');
		this.loadFeaturedBlogs();
		this.loadBlogs();
	}

	loadFeaturedBlogs() {
		this.blogService.getFeaturedBlogs().subscribe({
			next: (response) => {
				if (response.status) {
					this.featuredBlogs.set(response.data);
				}
			},
			error: (error) => console.error('Error loading featured blogs:', error),
		});
	}

	loadBlogs(append: boolean = false) {
		if (append) {
			this.isLoadingMore.set(true);
		} else {
			this.isLoading.set(true);
			this.currentPage.set(1);
		}
		this.errorMessage.set('');

		const params: any = {
			page: this.currentPage(),
			per_page: 9,
		};
		
		if (this.searchQuery()) {
			params.search = this.searchQuery();
		}
		if (this.selectedTag() && this.selectedTag() !== 'Recent') {
			params.tag = this.selectedTag().toLowerCase();
		}

		this.blogService.getPublishedBlogs(params).subscribe({
			next: (response) => {
				if (response.status) {
					const newPosts = response.data.posts || response.data;
					const pagination = response.data.pagination;
					
					if (append) {
						this.blogs.update(current => [...current, ...newPosts]);
					} else {
						this.blogs.set(newPosts);
					}
					
					if (pagination) {
						this.hasMore.set(pagination.has_more);
						this.totalPosts.set(pagination.total);
					} else {
						this.hasMore.set(false);
					}
				} else {
					this.errorMessage.set('Failed to load posts');
				}
				this.isLoading.set(false);
				this.isLoadingMore.set(false);
			},
			error: (error) => {
				console.error('Error loading posts:', error);
				this.errorMessage.set('Failed to load posts');
				this.isLoading.set(false);
				this.isLoadingMore.set(false);
			},
		});
	}

	loadMore() {
		if (this.hasMore() && !this.isLoadingMore()) {
			this.currentPage.update(p => p + 1);
			this.loadBlogs(true);
		}
	}

	onSearch(event: Event) {
		const input = event.target as HTMLInputElement;
		this.searchQuery.set(input.value);

		// Clear existing timer
		if (this.searchDebounceTimer) {
			clearTimeout(this.searchDebounceTimer);
		}

		// Set new timer for debounced search
		this.searchDebounceTimer = setTimeout(() => {
			this.loadBlogs();
		}, 500);
	}

	onTagChange(tag: string) {
		this.selectedTag.set(tag);
		this.loadBlogs();
	}

	getImageUrl(path: string): string {
		return path || '/images/placeholder-blog.jpg';
	}

	formatDate(date: string): string {
		return new Date(date).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		});
	}

	onImageError(event: Event) {
		const img = event.target as HTMLImageElement;
		if (!img.src.includes('data:image')) {
			img.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"%3E%3Crect fill="%23eaeae7" width="400" height="300"/%3E%3Ctext fill="%235d202d" font-family="system-ui" font-size="18" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
			img.onerror = null;
		}
	}
}
